import React, { useState } from 'react';
import { Button, Modal, DatePicker, Space, message, Dropdown } from 'antd';
import { DownloadOutlined, DownOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import type { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

export interface ExportOption {
  key: string;
  label: string;
  exportFn: (fromDate?: string, toDate?: string) => Promise<{ data: Blob }>;
  filename: string;
}

interface ExportButtonProps {
  options: ExportOption[];
  buttonText?: string;
  single?: boolean; // If true, just show a button (not dropdown)
}

const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

export const ExportButton: React.FC<ExportButtonProps> = ({
  options,
  buttonText = 'Export',
  single = false,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);
  const [selectedOption, setSelectedOption] = useState<ExportOption | null>(null);

  const handleExport = async () => {
    if (!selectedOption) return;

    setLoading(true);
    try {
      const fromDate = dateRange?.[0]?.toISOString();
      const toDate = dateRange?.[1]?.toISOString();

      const response = await selectedOption.exportFn(fromDate, toDate);
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `${selectedOption.filename}_${dateStr}.csv`;

      downloadBlob(response.data, filename);
      message.success(`${selectedOption.label} exported successfully`);
      setModalOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      message.error('Export failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (option: ExportOption) => {
    setSelectedOption(option);
    setDateRange(null);
    setModalOpen(true);
  };

  // Single button mode
  if (single && options.length === 1) {
    return (
      <>
        <Button icon={<DownloadOutlined />} onClick={() => openModal(options[0])}>
          {buttonText}
        </Button>
        <Modal
          title={`Export ${selectedOption?.label || ''}`}
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          onOk={handleExport}
          okText="Export"
          confirmLoading={loading}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <p>Select a date range for the export (optional):</p>
            <RangePicker
              style={{ width: '100%' }}
              value={dateRange}
              onChange={(dates) => setDateRange(dates)}
              allowClear
            />
            <p style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
              Leave empty to export all data.
            </p>
          </Space>
        </Modal>
      </>
    );
  }

  // Dropdown mode for multiple options
  const menuItems: MenuProps['items'] = options.map((opt) => ({
    key: opt.key,
    label: opt.label,
    onClick: () => openModal(opt),
  }));

  return (
    <>
      <Dropdown menu={{ items: menuItems }} trigger={['click']}>
        <Button icon={<DownloadOutlined />}>
          {buttonText} <DownOutlined />
        </Button>
      </Dropdown>
      <Modal
        title={`Export ${selectedOption?.label || ''}`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleExport}
        okText="Export"
        confirmLoading={loading}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <p>Select a date range for the export (optional):</p>
          <RangePicker
            style={{ width: '100%' }}
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            allowClear
          />
          <p style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
            Leave empty to export all data.
          </p>
        </Space>
      </Modal>
    </>
  );
};

export default ExportButton;
